import React, { useState, useRef, useCallback, useLayoutEffect } from "react";
import { gsap } from "gsap";
import styles from "./Timeline.module.scss";
import { DesktopLayout } from "./ui/DesktopLayout/DesktopLayout";
import { MobileLayout } from "./ui/MobileLayout/MobileLayout";
import { segments } from "./model/data";

const ANCHOR_ANGLE = -60;

const normalizeRotation = (from: number, to: number): number => {
  let normalized = to;
  while (normalized - from > 180) {
    normalized -= 360;
  }
  while (normalized - from < -180) {
    normalized += 360;
  }
  return normalized;
};

const Timeline: React.FC = () => {
  const [periodIndex, setPeriodIndex] = useState(0);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [transitioningToIndex, setTransitioningToIndex] = useState<number | null>(null);
  const [isLabelVisible, setIsLabelVisible] = useState(true);
  const [displayYears, setDisplayYears] = useState({ left: segments[0].startYear, right: segments[0].endYear });
  const dotsGroupRef = useRef<HTMLDivElement>(null);
  const yearsRef = useRef<HTMLDivElement>(null);
  const dotRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const yearTweenRef = useRef<gsap.core.Tween | null>(null);
  const yearsValueRef = useRef({ left: segments[0].startYear, right: segments[0].endYear });
  const rafRef = useRef<number | null>(null);

  const total = segments.length;
  const activeSegment = segments[periodIndex];

  const calculateRotation = (index: number): number => {
    const angle = (360 / total) * index;
    return ANCHOR_ANGLE - angle;
  };

  const [currentDialRotation, setCurrentDialRotation] = useState(calculateRotation(0));

  useLayoutEffect(() => {
    if (!dotsGroupRef.current) return;
    const initialRotation = calculateRotation(0);
    gsap.set(dotsGroupRef.current, { rotation: initialRotation, transformOrigin: "50% 50%" });
    setCurrentDialRotation(initialRotation);
  }, [total]);

  const getCurrentRotation = (): number => {
    if (dotsGroupRef.current) {
      const rotation = gsap.getProperty(dotsGroupRef.current, "rotation");
      return rotation ? (rotation as number) : calculateRotation(periodIndex);
    }
    return calculateRotation(periodIndex);
  };

  const setDotRef = useCallback((index: number, el: HTMLButtonElement | null) => {
    dotRefs.current[index] = el;
  }, []);

  const formatCounter = (current: number, total: number): string => {
    const currentStr = String(current + 1).padStart(2, "0");
    const totalStr = String(total).padStart(2, "0");
    return `${currentStr}/${totalStr}`;
  };

  const counterText = formatCounter(periodIndex, total);

  const transitionToPeriod = useCallback(
    (nextPeriodIndex: number) => {
      if (nextPeriodIndex === periodIndex) return;
      if (isAnimating) return;
      if (!dotsGroupRef.current || !yearsRef.current) return;

      const oldDotEl = dotRefs.current[periodIndex];
      const newDotEl = dotRefs.current[nextPeriodIndex];
      if (!oldDotEl || !newDotEl) return;

      setIsAnimating(true);
      setIsLabelVisible(false);
      setTransitioningToIndex(nextPeriodIndex);

      const targetSegment = segments[nextPeriodIndex];
      const targetStartYear = targetSegment.startYear;
      const targetEndYear = targetSegment.endYear;

      if (yearTweenRef.current) {
        yearTweenRef.current.kill();
      }

      yearsValueRef.current = {
        left: displayYears.left,
        right: displayYears.right,
      };

      yearTweenRef.current = gsap.to(yearsValueRef.current, {
        left: targetStartYear,
        right: targetEndYear,
        duration: 0.8,
        ease: "power2.inOut",
        onUpdate: () => {
          setDisplayYears({
            left: Math.round(yearsValueRef.current.left),
            right: Math.round(yearsValueRef.current.right),
          });
        },
        onComplete: () => {
          yearsValueRef.current = {
            left: targetStartYear,
            right: targetEndYear,
          };
          setDisplayYears({
            left: targetStartYear,
            right: targetEndYear,
          });
        },
      });

      const currentRotation = getCurrentRotation();
      const targetRotation = normalizeRotation(
        currentRotation,
        calculateRotation(nextPeriodIndex)
      );

      const tl = gsap.timeline({
        defaults: { ease: "power2.inOut" },
        onComplete: () => {
          setIsAnimating(false);
        },
      });

      tl.to(oldDotEl, { scale: 0.6, opacity: 0.6, duration: 0.15, ease: "power2.in" }, 0);
      tl.fromTo(
        newDotEl,
        { scale: 0.6, opacity: 0.6 },
        { scale: 1, opacity: 1, duration: 0.2, ease: "power2.out" },
        0
      );

      const rotationTween = gsap.to(dotsGroupRef.current, {
        rotation: targetRotation,
        duration: 0.8,
        ease: "power2.inOut",
        onUpdate: () => {
          if (rafRef.current !== null) return;
          rafRef.current = requestAnimationFrame(() => {
            if (dotsGroupRef.current) {
              const rotation = gsap.getProperty(dotsGroupRef.current, "rotation");
              if (rotation !== null) {
                setCurrentDialRotation(rotation as number);
              }
            }
            rafRef.current = null;
          });
        },
      });
      tl.add(rotationTween, 0);

      tl.call(() => {
        setPeriodIndex(nextPeriodIndex);
        setActiveSlideIndex(0);
      }, [], 0.8);

      tl.call(() => {
        setIsLabelVisible(true);
      }, [], 0.81);

      tl.call(() => {
        if (oldDotEl) {
          gsap.set(oldDotEl, { clearProps: "scale,opacity" });
        }
      }, [], 0.81);

      tl.call(() => {
        setTransitioningToIndex(null);
      }, [], 0.82);
    },
    [periodIndex, isAnimating, total, displayYears]
  );

  const handlePeriodPrev = () => {
    const nextIndex = periodIndex === 0 ? total - 1 : periodIndex - 1;
    transitionToPeriod(nextIndex);
  };

  const handlePeriodNext = () => {
    const nextIndex = periodIndex === total - 1 ? 0 : periodIndex + 1;
    transitionToPeriod(nextIndex);
  };

  const handleDotClick = (index: number) => {
    transitionToPeriod(index);
  };

  const handleSlideChange = (index: number) => {
    setActiveSlideIndex(index);
  };

  const dialRotation = calculateRotation(periodIndex);

  const periods = segments.map((s) => ({ id: s.id, title: s.title }));

  const pagerDots = segments.map((_, index) => ({
    index,
    isActive: index === periodIndex,
  }));


  return (
    <div className={styles.timeline}>
      <div className={styles.desktop}>
        <DesktopLayout
          total={total}
          periodIndex={periodIndex}
          dialRotation={dialRotation}
          groupRotation={currentDialRotation}
          periods={periods}
          transitioningToIndex={transitioningToIndex}
          isLabelVisible={isLabelVisible}
          yearLeft={displayYears.left}
          yearRight={displayYears.right}
          events={activeSegment.events}
          activeSlideIndex={activeSlideIndex}
          onSlideChange={handleSlideChange}
          count={counterText}
          onPeriodPrev={handlePeriodPrev}
          onPeriodNext={handlePeriodNext}
          onDotClick={handleDotClick}
          setDotRef={setDotRef}
          isNextDisabled={periodIndex === total - 1}
          isAnimating={isAnimating}
          dotsGroupRef={dotsGroupRef}
          yearsRef={yearsRef}
        />
      </div>

      <div className={styles.mobile}>
        <MobileLayout
          yearLeft={String(displayYears.left)}
          yearRight={String(displayYears.right)}
          events={activeSegment.events}
          activeSlideIndex={activeSlideIndex}
          onSlideChange={handleSlideChange}
          periodIndex={periodIndex}
          pagerDots={pagerDots}
          count={counterText}
          onPeriodPrev={handlePeriodPrev}
          onPeriodNext={handlePeriodNext}
          onPeriodSelect={handleDotClick}
          isAnimating={isAnimating}
        />
      </div>
    </div>
  );
};

export default Timeline;

